import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Divider, notification, Row } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import LogoPoweredByBiologis from '../../../foundation/assets/svgs/LogoPoweredByBiologis';
import {
  DISABLED_TEXT,
  OVERRULED_RED,
} from '../../../foundation/config/colors';
import { displayUnAuthorizedNotification } from '../../../foundation/utils/notifications';
import {
  useCreateVariantAssessmentConfirmationMutation,
  useGetInterpretationSnapshotsQuery,
  useGetVariantAssessmentArgumentsQuery,
  useGetVariantAssessmentStagesQuery,
  useGetVariantInterpretationZoneQuery,
  useUpdateClinicalInterpretationByPostPMutation,
  useUpdateVariantAssessmentStageMutation,
} from '../../api/variantApi';
import { Variant } from '../../variant_check/redux/types';
import DetailCard from '../components/DetailCard';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../constants';
import { useHasRunningRefresh } from '../hooks/useHasRunningRefresh';
import { useVariantAccess } from '../hooks/useVariantAccess';
import type { InterpretationSnapshotCreateRequest } from '../redux';
import { selectVariantLock, VariantStageStatus } from '../redux';
import { checkIsCnvVariant, StageFilterOptions } from '../utils';
import ClinicalInterpretationModal from './ClinicalInterpretationModal';
import {
  handleRowProps,
  structureArgumentsDataForInterpretationZone,
  structureArgumentsDataForStage,
} from './helpers/argumentsHelpers';
import { StructuredArgument } from './helpers/structureArgumentsData';
import InterpretationHeatmapSlider from './InterpretationHeatmapSlider';
import InterpretationSnapshotsList from './InterpretationSnapshotsList';
import RecalculateArgumentsButton from './RecalculateArgumentsButton';
import { getInterpretationSnapshotCapture } from './snapshotCapture';
import StageArgumentsModal from './StageArgumentsModal';

type InterpretationProps = {
  variant: Variant;
  stepLabel?: string;
  stepStatus?: 'not_started' | 'accepted' | 'revised';
  stepId?: number;
  stepType?: string;
  showSnapshots?: boolean;
  onBeforeRevision?: () => Promise<InterpretationSnapshotCreateRequest | null>;
  onAfterRevision?: (
    preparedSnapshot: InterpretationSnapshotCreateRequest | null,
  ) => Promise<boolean>;
};

const Interpretation = ({
  variant,
  stepLabel,
  stepStatus,
  stepId,
  stepType,
  showSnapshots = false,
  onBeforeRevision,
  onAfterRevision,
}: InterpretationProps) => {
  const [updateVariantAssessmentStage] =
    useUpdateVariantAssessmentStageMutation();
  // Mutation hooks return [trigger, result] tuples, replacing the old
  // dispatch(thunk(...)).unwrap() pattern
  const [createVariantAssessmentConfirmation] =
    useCreateVariantAssessmentConfirmationMutation();
  const [updateClinicalInterpretationByPostP] =
    useUpdateClinicalInterpretationByPostPMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const [isArgumentsModalOpen, setIsArgumentsModalOpen] = useState(false);

  const [isSettingByPostP, setIsSettingByPostP] = useState(false);

  const [
    isClinicalInterpretationModalOpen,
    setIsClinicalInterpretationModalOpen,
  ] = useState(false);

  const [stageArgumentsData, setStageArgumentsData] = useState<
    StructuredArgument[]
  >([]);

  const { canEditWorkflow } = useVariantAccess();
  const lock = useSelector(selectVariantLock);
  const hasRunningRefresh = useHasRunningRefresh(variant.id);

  // RTK Query hook for interpretation zone data
  const { data: interpretationZoneData, refetch: refetchInterpretationZone } =
    useGetVariantInterpretationZoneQuery(
      { variant_id: variant.id },
      { skip: !variant.id },
    );

  const { data: interpretationSnapshots = [] } =
    useGetInterpretationSnapshotsQuery(
      { variant_id: variant.id },
      { skip: !showSnapshots || !variant.id },
    );

  const { data: argumentsForClassification } =
    useGetVariantAssessmentArgumentsQuery(
      { variant_id: variant.id },
      { skip: !variant.id },
    );

  // Variant-level consequence terms (not per-argument).
  const consequence = useMemo(() => {
    return variant.consequence;
  }, [variant.consequence]);

  const variantTranscriptConsequences = useMemo(() => {
    return variant.variant_transcript_consequences ?? [];
  }, [variant.variant_transcript_consequences]);

  const isCnvVariant = useMemo(() => {
    return checkIsCnvVariant(
      variant.variant_type,
      consequence,
      variantTranscriptConsequences,
    );
  }, [variant.variant_type, consequence, variantTranscriptConsequences]);

  useEffect(() => {
    if (stepType && argumentsForClassification) {
      setStageArgumentsData(
        structureArgumentsDataForStage(
          argumentsForClassification,
          stepType,
          consequence,
          {
            isCnvVariant,
          } as StageFilterOptions,
        ),
      );
    }
  }, [argumentsForClassification, stepType, consequence, isCnvVariant]);

  // Read stages straight from the query hook's cache rather than a selector
  const { data: workflowStages } = useGetVariantAssessmentStagesQuery(
    { variant_id: variant?.id },
    { skip: !variant?.id },
  );

  const renderInterpretationsRow = useCallback(
    (label: string, value?: string) => {
      return (
        <>
          <Row style={{ width: '100%' }}>
            <Col flex={'55%'} className="variant-summary__field-label">
              {label}
            </Col>
            <Col className="variant-summary__field-value">{value}</Col>
          </Row>
          <Divider style={{ margin: '5px 0px' }} />
        </>
      );
    },
    [],
  );

  const handleSetByPostP = async () => {
    try {
      if (interpretationZoneData?.clinical_interpretation) {
        if (canEditWorkflow) {
          setIsSettingByPostP(true);

          // RTK Query mutation â€“ invalidatesTags handles the InterpretationZone
          // and VariantStages refetch automatically (no manual fetchAllVariantStages needed).
          const response = await updateClinicalInterpretationByPostP({
            variant_id: variant.id,
            requestData: {
              clinical_interpretation:
                interpretationZoneData?.clinical_interpretation,
            },
          }).unwrap();

          if (response.clinical_interpretation) {
            // RTK Query will refetch InterpretationZone via tag invalidation.
            // Manually refetch here so the UI updates before the success toast.
            await refetchInterpretationZone();

            notification.success({
              message: NOTIFICATION_MESSAGES.SUCCESS,
              description:
                NOTIFICATION_MESSAGES.CLINICAL_INTERPRETATION_UPDATED,
            });
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSettingByPostP(false);
    }
  };

  const renderClinicalInterpretationRow = useCallback(
    (value?: string) => {
      const canEdit = canEditWorkflow && stepLabel === 'Interpretation';
      const showSetByPostP =
        value?.includes('classified') &&
        stepLabel === 'Interpretation' &&
        !!interpretationZoneData?.post_p;
      return (
        <>
          <Row align={'middle'} style={{ width: '100%' }}>
            <Col flex={'55%'} className="variant-summary__field-label">
              Clinical interpretation
            </Col>
            <Col className="variant-summary__field-value">
              {showSetByPostP ? (
                <Button
                  type="primary"
                  className="interpretation__set-by-post-p-btn"
                  onClick={handleSetByPostP}
                  loading={isSettingByPostP}
                  disabled={!canEditWorkflow}
                >
                  Set by post_P
                </Button>
              ) : (
                <span
                  onClick={
                    canEdit ? handleClinicalInterpretationModalOpen : undefined
                  }
                  style={{
                    textDecoration: canEdit ? 'underline' : 'none',
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    color: interpretationZoneData?.overruled_by_user
                      ? OVERRULED_RED
                      : canEdit
                        ? undefined
                        : DISABLED_TEXT,
                    pointerEvents: canEdit ? 'auto' : 'none',
                  }}
                >
                  {value || '-'}
                </span>
              )}
            </Col>
          </Row>
          <Divider style={{ margin: '5px 0px' }} />
        </>
      );
    },
    [
      canEditWorkflow,
      stepLabel,
      interpretationZoneData?.post_p,
      interpretationZoneData?.overruled_by_user,
    ],
  );

  const statusChangeBtnLabel = useMemo(() => {
    if (stepStatus === 'accepted') {
      return 'Revise';
    } else if (stepStatus === 'revised') {
      return 'Accept';
    }
    return 'Accept';
  }, [stepStatus]);

  const handleStatusChange = async (
    status: VariantStageStatus,
    stepId: number,
  ) => {
    try {
      setIsLoading(true);

      const remoteSnapshotCapture = onBeforeRevision
        ? null
        : getInterpretationSnapshotCapture();
      let preparedSnapshot: InterpretationSnapshotCreateRequest | null = null;
      const isRevisionOfAcceptedStage =
        status === 'revised' && stepStatus === 'accepted';

      if (isRevisionOfAcceptedStage) {
        if (onBeforeRevision) {
          preparedSnapshot = await onBeforeRevision();
        } else if (remoteSnapshotCapture) {
          preparedSnapshot = await remoteSnapshotCapture.prepare();
        }
      }

      const captureSnapshotInFrontend = Boolean(
        isRevisionOfAcceptedStage && preparedSnapshot,
      );

      const stageUpdate = await updateVariantAssessmentStage({
        id: stepId,
        status,
        variant_id: variant.id,
        capture_snapshot_in_frontend: captureSnapshotInFrontend,
      }).unwrap();

      if (stageUpdate.snapshot_requested && preparedSnapshot) {
        const snapshotCreated = onAfterRevision
          ? await onAfterRevision(preparedSnapshot)
          : remoteSnapshotCapture
            ? await remoteSnapshotCapture.persist(preparedSnapshot)
            : false;
        if (!snapshotCreated) {
          notification.warning({
            message: NOTIFICATION_MESSAGES.SNAPSHOT_CREATION_FAILED,
            description:
              NOTIFICATION_MESSAGES.SNAPSHOT_CREATION_FAILED_DESCRIPTION,
          });
        }
      }

      // updateVariantAssessmentStage already invalidates specific variant tags,
      // so no manual fetchAllVariantStages dispatch is needed.
      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS,
        description: NOTIFICATION_MESSAGES.STAGE_UPDATED,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChangeBtnHandler = () => {
    // Prevent unauthorized users from changing stage status
    if (!canEditWorkflow) {
      return;
    }
    if (stepId) {
      const status = stepStatus === 'accepted' ? 'revised' : 'accepted';
      return handleStatusChange(status, stepId);
    }
  };

  const handleConfirmation = async () => {
    if (!canEditWorkflow) {
      return;
    }
    try {
      setIsConfirming(true);
      const response = await createVariantAssessmentConfirmation({
        variant_id: variant.id,
      }).unwrap();
      if (response.created) {
        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: NOTIFICATION_MESSAGES.CONFIRMATION_CREATED,
        });
      } else {
        notification.info({
          message: NOTIFICATION_MESSAGES.INFO,
          description: NOTIFICATION_MESSAGES.CONFIRMATION_ALREADY_EXISTS,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsConfirming(false);
    }
  };

  const stepClassName = useMemo(() => {
    if (stepStatus === 'accepted') {
      return 'l-interpretation__acceptance-wrapper--accepted';
    } else if (stepStatus === 'revised') {
      return 'l-interpretation__acceptance-wrapper--revision';
    } else {
      return 'l-interpretation__acceptance-wrapper--not-started';
    }
  }, [stepStatus]);

  const renderStepStatus = useMemo(() => {
    if (stepStatus === 'not_started') {
      return 'in progress';
    } else if (stepStatus === 'revised') {
      return 'in revision';
    } else {
      return 'accepted';
    }
  }, [stepStatus]);

  const isAllPriorStagesAccepted = useMemo(() => {
    return (workflowStages ?? [])
      .filter(
        (stage) => stage.stage_type !== VARIANT_STAGE_TYPES.INTERPRETATION,
      )
      .every((stage) => stage.status === 'accepted');
  }, [workflowStages]);

  const isStatusBtnVisibleForInterpretation = useMemo(() => {
    const isClinicalInterpretationClassified =
      interpretationZoneData?.clinical_interpretation?.includes('classified');

    return (
      stepLabel === 'Interpretation' &&
      stepStatus &&
      statusChangeBtnLabel &&
      isAllPriorStagesAccepted &&
      interpretationZoneData &&
      !isClinicalInterpretationClassified
    );
  }, [
    interpretationZoneData,
    stepLabel,
    stepStatus,
    statusChangeBtnLabel,
    isAllPriorStagesAccepted,
  ]);

  const handleArgumentsModalClose = () => {
    setIsArgumentsModalOpen(false);
  };

  const handleArgumentsModalOpen = () => {
    setIsArgumentsModalOpen(true);
  };

  const handleClinicalInterpretationModalClose = () => {
    setIsClinicalInterpretationModalOpen(false);
  };

  const handleClinicalInterpretationModalOpen = () => {
    if (!canEditWorkflow) {
      displayUnAuthorizedNotification();
      return;
    }
    // Allow editing only on Interpretation stage
    if (stepLabel !== 'Interpretation') {
      return;
    }
    setIsClinicalInterpretationModalOpen(true);
  };

  const selectedStageArguments = useMemo(() => {
    return structureArgumentsDataForInterpretationZone(
      argumentsForClassification ?? [],
      stepType,
    );
  }, [argumentsForClassification, stepType, stageArgumentsData]);

  const argumentsRenderer = useMemo(() => {
    const argumentsData: any[] = [];

    Object.entries(selectedStageArguments).forEach(([sectionId, items]) => {
      argumentsData.push(
        <div key={sectionId} className="interpretation__arguments-wrapper">
          <div className="interpretation__arguments-section-title">
            {sectionId}
          </div>
          <div className="interpretation__arguments-section-items-wrapper">
            {items.map((item) => (
              <div
                key={item.id}
                className="interpretation__arguments-section-item"
              >
                <div className="interpretation__arguments-section-item-classification">
                  <div>
                    <span
                      className={` classification-margin-right ${`classification-${item.acmg_class.toLowerCase()}`}`}
                    >
                      {item.acmg_class.toLowerCase()}
                    </span>
                    <span
                      className={`classification-margin-right ${`classification-${item.acmg_class.toLowerCase()}`}`}
                    >
                      {item.acmg_grade.toLowerCase()}
                    </span>
                  </div>
                  <div>
                    <span
                      className={`interpretation__classification-rule-code`}
                    >
                      {item.acmg_rule_code}
                    </span>
                  </div>
                </div>
                <div className="interpretation__arguments-section-item-classification-text">
                  {item.text}
                </div>
              </div>
            ))}
          </div>
        </div>,
      );
    });

    return argumentsData;
  }, [selectedStageArguments, stageArgumentsData]);

  return (
    <>
      {isArgumentsModalOpen && stepType && stepLabel && (
        <StageArgumentsModal
          closeModal={handleArgumentsModalClose}
          variantId={variant.id.toString()}
          variant_consequence={consequence}
          variant_type={variant.variant_type}
          variant_transcript_consequences={variantTranscriptConsequences}
          stage={stepType}
          stageLabel={stepLabel}
          onRow={handleRowProps}
          dataSource={stageArgumentsData}
        />
      )}
      {isClinicalInterpretationModalOpen &&
        interpretationZoneData?.evaluation_entity_id && (
          <ClinicalInterpretationModal
            onClose={handleClinicalInterpretationModalClose}
            evaluationEntityId={interpretationZoneData.evaluation_entity_id}
            variantId={variant.id}
            defaultValue={interpretationZoneData?.clinical_interpretation}
          />
        )}
      <Col flex={'22%'} className="l-interpretation">
        <div className="l-interpretation__content">
          <DetailCard>
            <h3>Interpretation zone</h3>
            {stepLabel && stepStatus && (
              <>
                <div
                  className={`l-interpretation__acceptance-wrapper
              ${stepClassName}`}
                >
                  <div>{stepLabel}</div>
                  <div>{renderStepStatus}</div>
                </div>
                <Divider />
              </>
            )}

            {((!stepLabel && !stepStatus) ||
              stepLabel === 'Interpretation') && (
              <div className="interpretation__arguments-for-classification-wrapper">
                <InterpretationHeatmapSlider
                  postProbability={interpretationZoneData?.post_p}
                />

                <Divider style={{ margin: '5px 0px' }} />
                {renderInterpretationsRow(
                  'Posterior probability',
                  interpretationZoneData?.post_p,
                )}
                {renderInterpretationsRow(
                  'Classification',
                  interpretationZoneData?.classification,
                )}

                {renderClinicalInterpretationRow(
                  interpretationZoneData?.clinical_interpretation,
                )}
              </div>
            )}

            {stepLabel &&
              stepStatus &&
              stepType &&
              stepLabel !== 'Interpretation' && (
                <>
                  <div className="interpretation__arguments-for-classification-wrapper">
                    <h3 className="interpretation__arguments-for-classification-wrapper-title">
                      Arguments for classification
                    </h3>
                    {argumentsRenderer}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Button
                        type="primary"
                        variant="filled"
                        onClick={handleArgumentsModalOpen}
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        className="interpretation__arguments-for-classification-wrapper-btn"
                        disabled={!canEditWorkflow || hasRunningRefresh}
                        title={
                          !canEditWorkflow
                            ? 'You do not have permission to add arguments.'
                            : hasRunningRefresh
                              ? 'Refresh & recalculate is running. Please wait until it finishes.'
                              : lock
                                ? `Variant is locked by ${lock.locked_by}. Cannot add arguments.`
                                : undefined
                        }
                      >
                        Arguments
                      </Button>
                      <RecalculateArgumentsButton stageType={stepType} />
                    </div>
                  </div>
                  <Divider />
                </>
              )}

            {/*
          We want to render this button for all the stages except Summary.
          For Interpration stage we don't want to show this button if the clinical interpretaion is to be classified or null.
        */}
            {((stepLabel &&
              stepStatus &&
              statusChangeBtnLabel &&
              stepLabel !== 'Interpretation') ||
              isStatusBtnVisibleForInterpretation) && (
              <div className="l-interpretation__action-btn-wrapper">
                {stepStatus === 'accepted' &&
                  stepLabel === 'Interpretation' && (
                    <Button
                      type="primary"
                      className="l-interpretation__action-btn"
                      onClick={handleConfirmation}
                      loading={isConfirming}
                      disabled={
                        !canEditWorkflow || isConfirming || hasRunningRefresh
                      }
                      title={
                        hasRunningRefresh
                          ? 'Refresh & recalculate is running. Please wait until it finishes.'
                          : undefined
                      }
                    >
                      Confirm
                    </Button>
                  )}
                <Button
                  type="primary"
                  className="l-interpretation__action-btn"
                  onClick={handleStatusChangeBtnHandler}
                  loading={isLoading}
                  disabled={!canEditWorkflow || isLoading || hasRunningRefresh}
                  title={
                    hasRunningRefresh
                      ? 'Refresh & recalculate is running. Please wait until it finishes.'
                      : undefined
                  }
                >
                  {statusChangeBtnLabel}
                </Button>
              </div>
            )}
          </DetailCard>
          {showSnapshots && (
            <InterpretationSnapshotsList snapshots={interpretationSnapshots} />
          )}
        </div>
        <div className="interpretation__powered-by">
          <LogoPoweredByBiologis />
        </div>
      </Col>
    </>
  );
};

export default Interpretation;
